import { useEffect, useRef, useState } from 'react';
import { Instagram, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';

// 2026-11-01 (owner) — tall 9:16 recap for Instagram / TikTok stories. Phones
// get the share sheet with the image; desktop downloads it to post later.
// The PNG is prefetched so navigator.share() runs inside the tap (iOS Safari
// rejects a share that starts after a slow await with NotAllowedError).
const fetchFile = async (imagePath, fileName) => {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}${imagePath}`);
  if (!res.ok) throw new Error('story fetch failed');
  return new File([await res.blob()], fileName, { type: 'image/png' });
};

const download = (file) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(file);
  a.download = file.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  toast.success('Story image saved', { description: 'Post it to your Instagram or TikTok story.' });
};

export default function StoryShareButton({ imagePath, fileName = 'fws-story.png', title, testid = 'story-share-btn' }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    let alive = true;
    fileRef.current = null;
    fetchFile(imagePath, fileName).then((f) => { if (alive) fileRef.current = f; }).catch(() => {});
    return () => { alive = false; };
  }, [imagePath, fileName]);

  const onClick = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    let file = fileRef.current;
    try {
      file = file || await fetchFile(imagePath, fileName);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title, files: [file] });
      } else {
        download(file);
      }
    } catch (err) {
      if (err?.name === 'NotAllowedError' && file) download(file);
      else if (err?.name !== 'AbortError') toast.error("Couldn't make the story image — try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button type="button" variant="outline" size="sm" onClick={onClick} disabled={busy}
      className="mt-4 w-full rounded-full" data-testid={testid} aria-label="Share to Story">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Instagram className="h-4 w-4" />}
      <span className="ml-2">Share to Story</span>
    </Button>
  );
}
